// src/pages/TagManager.jsx
import React, { useEffect, useState } from "react";
import Tag from "../components/Tag";
import axiosBackend from "../api/axiosBackend";
import { DEFAULT_TAG_COLOR } from "utils/tagColor";

// shadcn
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle as DialogTitleUI,
  DialogDescription,
} from "components/ui/dialog";

function TagManager() {
  const [tags, setTags] = useState([]);
  const [editTag, setEditTag] = useState(null);
  const [tagName, setTagName] = useState("");
  const [tagColor, setTagColor] = useState(DEFAULT_TAG_COLOR);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const fetchTags = async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const response = await axiosBackend.get("/tags/get");
        setTags(response.data);
      } catch (error) {
        console.error("Error fetching tags:", error);
        setErrorMsg("אירעה שגיאה בעת טעינת התגיות");
      } finally {
        setLoading(false);
      }
    };
    fetchTags();
  }, []);

  const handleEditClick = (tag) => {
    setEditTag(tag);
    setTagName(tag.name);
    setTagColor(tag.color || DEFAULT_TAG_COLOR);
  };

  const handleSaveClick = async () => {
    if (!editTag) return;
    if (!tagName.trim()) return; // simple guard; keep logic minimal
    setSaving(true);
    try {
      const response = await axiosBackend.put(`/tags/${editTag._id}`, {
        name: tagName.trim(),
        color: tagColor,
      });
      setTags((prev) => prev.map((t) => (t._id === editTag._id ? response.data : t)));
      setEditTag(null);
      setTagName("");
    } catch (error) {
      console.error("Error updating tag:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div dir="rtl">
      <Card className="mx-auto max-w-5xl">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-foreground">ניהול תגיות</CardTitle>
        </CardHeader>

        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">טוען תגיות…</p>
          ) : errorMsg ? (
            <p className="text-sm text-destructive">{errorMsg}</p>
          ) : tags.length === 0 ? (
            <p className="text-sm text-muted-foreground">לא נמצאו תגיות.</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {tags.map((tag) => (
                <div
                  key={tag._id}
                  className="flex items-center justify-between rounded-lg border p-2"
                >
                  <Tag name={tag.name} color={tag.color} />
                  <Button variant="secondary" size="sm" onClick={() => handleEditClick(tag)}>
                    עריכה
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog (uses your existing editTag/tagName state & save handler) */}
      <Dialog open={!!editTag} onOpenChange={(open) => !open && setEditTag(null)}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitleUI>עריכת תג</DialogTitleUI>
            <DialogDescription>עדכן את שם התג ולחץ שמור.</DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-3">
            <Input
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              placeholder="שם תג"
              autoFocus
            />

            <div className="flex items-center gap-3">
              <Label htmlFor="tag-color" className="text-muted-foreground">
                צבע
              </Label>
              <input
                id="tag-color"
                type="color"
                value={tagColor}
                onChange={(e) => setTagColor(e.target.value)}
                className="h-9 w-14 cursor-pointer rounded-md border bg-background p-1"
              />
              <Tag name={tagName || "תצוגה מקדימה"} color={tagColor} />
            </div>

          </DialogBody>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditTag(null)} disabled={saving}>
              ביטול
            </Button>
            <Button onClick={handleSaveClick} disabled={saving || !tagName.trim()}>
              {saving ? "שומר…" : "שמור"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export default TagManager;
